import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import Header from '../../Components/Header';
import aquinoImage from '../../Images/aquino.svg';
import aquinasLogo from '../../Images/aquinas-logo.svg';
import renewAccessImage from '../../Images/renew-access.png';
import renewAccessSpanishImage from '../../Images/renew-access-es.png';
import topRightImage from '../../Images/top-right.png';
import { quoteResources } from './resources';
import './QuotesPage.css';

const quoteCopy = {
	english: {
		headerTitle: [
			'Renew your access to the',
			'Aquinas Online Library and',
			'discover a world of new',
			'possibilities!',
		],
		introTitle:
			"Congratulations on another year with the Aquinas Online Library as a strategic partner in your student's education!",
		introText:
			"We're thrilled to share with you the exciting new features we've added to our platform this year. We've been working hard to bring you an even richer and more complete experience!",
		highlights: 'New features and highlights:',
		features: [
			[
				'Aquino IA:',
				"Our new smart virtual assistant is here to answer your students' questions instantly. A true “bookworm” who knows everything!",
			],
			[
				'Strategic alliances:',
				'We have established agreements with prestigious publishers such as Cengage - Gale, which allow us to offer you access to exclusive and cutting-edge material.',
			],
			[
				'Optimized user experience:',
				"We've redesigned our platform to make it even more intuitive and adaptable to your students' needs. Navigating and finding resources has never been easier!",
			],
		],
		resourcesTitle: 'New academic resources',
		resourcesIntro:
			'By renewing your contract, your academic community will gain access to a wide range of new resources, including:',
		tableTitle: 'The Database Collection Contains:',
		emptyTable: 'Select resources to include them in this quote.',
		afterTable:
			'We are confident that these improvements and new resources will further boost the academic success of your students.',
		renewTitle: 'Renew your access today!',
		renewLines: [
			"Don't miss the opportunity to continue enjoying all the benefits offered by the Aquinas Online Library.",
			'Renew your subscription now and enter a world of knowledge and possibilities!',
			'We would also love to schedule a meeting with our librarian, María del Pilar Mora, to refresh your',
			'knowledge, answer any questions you may have about the platform, and show you the new features.',
		],
		renewClosing: 'I look forward to hearing from you!',
		footer: [
			"If you have any questions or need assistance, please don't hesitate to contact us at",
			"We're here to help you every step of the way!",
		],
	},
	spanish: {
		headerTitle: [
			'¡Renueva tu acceso en',
			'Aquinas Online Library y',
			'descubre un mundo de',
			'nuevas posibilidades!',
		],
		introTitle:
			'¡Enhorabuena por otro año con Aquinas Online Library como socio estratégico en la educación de tu alumno!',
		introText:
			'Estamos encantados de compartir con vosotros las emocionantes nuevas funciones que hemos añadido a nuestra plataforma este año. ¡Hemos estado trabajando duro para ofrecerte una experiencia aún más enriquecedora y completa!',
		highlights: 'Nuevas características y aspectos destacados:',
		features: [
			[
				'Aquino IA:',
				'Nuestro nuevo asistente virtual inteligente está aquí para responder a las preguntas de tus estudiantes al instante. ¡Un verdadero “ratón de biblioteca” que lo sabe todo!',
			],
			[
				'Alianzas estratégicas:',
				'Hemos establecido acuerdos con editoriales prestigiosas como Cengage - Gale, que nos permiten ofrecerte acceso a material exclusivo y de vanguardia.',
			],
			[
				'Experiencia de usuario optimizada:',
				'Hemos rediseñado nuestra plataforma para hacerla aún más intuitiva y adaptable a las necesidades de tus estudiantes. ¡Navegar y encontrar recursos nunca ha sido tan fácil!',
			],
		],
		resourcesTitle: 'Nuevos recursos académicos',
		resourcesIntro:
			'Al renovar tu contrato, tu comunidad académica tendrá acceso a una amplia gama de nuevos recursos, incluyendo:',
		tableTitle: 'La Colección de bases de datos Contiene',
		emptyTable:
			'Selecciona los recursos que deseas incluir en esta cotización.',
		afterTable:
			'Estamos seguros de que estas mejoras y nuevos recursos impulsarán aún más el éxito académico de tus estudiantes.',
		renewTitle: '¡Renueva tu acceso hoy mismo!',
		renewLines: [
			'No pierdas la oportunidad de seguir disfrutando de todos los beneficios que ofrece la Aquinas Online Library.',
			'¡Renueva tu contrato ahora y entra en un mundo de conocimientos y posibilidades!',
			'Además, nos encantaría concertar una reunión con nuestra bibliotecaria, María del Pilar Mora, para',
			'refrescar, responder a cualquier pregunta sobre la plataforma y mostrar las nuevas funciones.',
		],
		renewClosing: '¡Espero saber de ti!',
		footer: [
			'Si tienes alguna pregunta o necesitas ayuda, no dudes en comunicarte con nosotros a',
			'¡Estamos aquí para ayudarte en cada paso del camino!',
		],
	},
};

const chunk = (items, size) => {
	const rows = [];
	for (let index = 0; index < items.length; index += size) {
		rows.push(items.slice(index, index + size));
	}
	return rows;
};

const EXPORT_WIDTH = 4250;
const MAX_CANVAS_SIDE = 30000;
const MAX_CANVAS_PIXELS = 100000000;

const waitForDocumentAssets = async (element) => {
	if (document.fonts?.ready) await document.fonts.ready;

	await Promise.all(
		Array.from(element.querySelectorAll('img')).map((image) => {
			if (image.complete)
				return image.decode
					? image.decode().catch(() => undefined)
					: Promise.resolve();

			return new Promise((resolve) => {
				image.addEventListener('load', resolve, { once: true });
				image.addEventListener('error', resolve, { once: true });
			});
		}),
	);
};

const getExportScale = (element) => {
	const width = element.scrollWidth;
	const height = element.scrollHeight;
	const targetScale = EXPORT_WIDTH / width;
	const sideLimit = MAX_CANVAS_SIDE / Math.max(width, height);
	const pixelLimit = Math.sqrt(MAX_CANVAS_PIXELS / (width * height));

	return Math.min(targetScale, sideLimit, pixelLimit);
};

const QuoteDocument = ({ documentRef, language, mode, resources }) => {
	const orderedResources = quoteResources.filter((resource) =>
		resources.includes(resource),
	);
	const rows = chunk(orderedResources, 4);
	const copy = quoteCopy[language];
	const isResourcesMode = mode === 'resources';

	return (
		<article
			className={`quote-document${isResourcesMode ? ' quote-document-resources' : ''}`}
			ref={documentRef}
		>
			{!isResourcesMode && (
				<>
					<header className='quote-top'>
						<div>
							<img
								className='quote-brand'
								src={aquinasLogo}
								alt='Aquinas Online Library'
							/>
							<h1>
								{copy.headerTitle.map((line) => (
									<React.Fragment key={line}>
										{line}
										<br />
									</React.Fragment>
								))}
							</h1>
						</div>
						<img className='quote-hero' src={topRightImage} alt='' />
					</header>

					<section className='quote-feature-panel'>
						<div className='quote-intro'>
							<h2>{copy.introTitle}</h2>
							<p>{copy.introText}</p>
							<strong>{copy.highlights}</strong>
							<img className='quote-art' src={aquinoImage} alt='' />
						</div>
						<div className='quote-feature-list'>
							{copy.features.map(([title, description]) => (
								<div className='quote-feature' key={title}>
									<span />
									<p>
										<strong>{title}</strong> {description}
									</p>
								</div>
							))}
						</div>
					</section>
				</>
			)}

			<section className='quote-resources'>
				<div className='quote-section-title'>
					<h2>{copy.resourcesTitle}</h2>
					{!isResourcesMode && <p>{copy.resourcesIntro}</p>}
				</div>
				<div className='quote-table-title'>
					<strong>Aquinas Online Library:</strong>&nbsp; {copy.tableTitle}
				</div>
				{orderedResources.length ? (
					<table>
						<tbody>
							{rows.map((row, rowIndex) => (
								<tr key={rowIndex}>
									{row.map((resource) => (
										<td key={resource}>{resource}</td>
									))}
									{Array.from({ length: 4 - row.length }).map((_, index) => (
										<td aria-hidden='true' key={`empty-${index}`} />
									))}
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<div className='quote-empty-table'>{copy.emptyTable}</div>
				)}
			</section>

			{!isResourcesMode && (
				<>
					<p className='quote-after-table'>{copy.afterTable}</p>

					<section className='quote-renew-box'>
						<img
							className='quote-renew-image'
							src={
								language === 'spanish'
									? renewAccessSpanishImage
									: renewAccessImage
							}
							alt={copy.renewTitle}
						/>
						<p>
							{copy.renewLines.map((line) => (
								<React.Fragment key={line}>
									{line}
									<br />
								</React.Fragment>
							))}
							<a href='mailto:librarian@aquinasnetwork.com'>
								{copy.renewClosing}
							</a>
						</p>
					</section>

					<footer className='quote-footer'>
						<p>
							{copy.footer[0]}
							<br />
							<a href='mailto:librarian@aquinasnetwork.com'>
								librarian@aquinasnetwork.com.
							</a>{' '}
							{copy.footer[1]}
						</p>
						<img
							className='quote-footer-logo'
							src={aquinasLogo}
							alt='Aquinas Online Library'
						/>
					</footer>
				</>
			)}
		</article>
	);
};

const QuotesPage = () => {
	const navigate = useNavigate();
	const quoteDocumentRef = useRef(null);
	const [selectedResources, setSelectedResources] = useState([]);
	const [search, setSearch] = useState('');
	const [language, setLanguage] = useState('english');
	const [quoteMode, setQuoteMode] = useState('renew');
	const [isExporting, setIsExporting] = useState(false);

	useEffect(() => {
		if (!localStorage.name || !localStorage.email) navigate('/');
	}, [navigate]);

	const filteredResources = useMemo(() => {
		const query = search.trim().toLowerCase();
		return query
			? quoteResources.filter((resource) =>
					resource.toLowerCase().includes(query),
				)
			: quoteResources;
	}, [search]);

	const toggleResource = (resource) => {
		setSelectedResources((current) =>
			current.includes(resource)
				? current.filter((item) => item !== resource)
				: [...current, resource],
		);
	};

	const exportAsJpeg = async () => {
		if (!quoteDocumentRef.current || !selectedResources.length) return;

		setIsExporting(true);
		try {
			await waitForDocumentAssets(quoteDocumentRef.current);
			const exportScale = getExportScale(quoteDocumentRef.current);
			const canvas = await html2canvas(quoteDocumentRef.current, {
				backgroundColor: '#ffffff',
				logging: false,
				scale: exportScale,
				useCORS: true,
				width: quoteDocumentRef.current.scrollWidth,
				height: quoteDocumentRef.current.scrollHeight,
				windowWidth: quoteDocumentRef.current.scrollWidth,
				windowHeight: quoteDocumentRef.current.scrollHeight,
				onclone: (clonedDocument) => {
					const clonedQuote = clonedDocument.querySelector('.quote-document');
					if (clonedQuote) clonedQuote.classList.add('quote-document-export');
				},
			});
			const blob = await new Promise((resolve) =>
				canvas.toBlob(resolve, 'image/jpeg', 1),
			);
			if (!blob) throw new Error('The JPEG image could not be created.');

			const link = document.createElement('a');
			link.download = `aquinas-${quoteMode === 'renew' ? 'renewal' : 'resources'}-quote-${new Date().toISOString().slice(0, 10)}.jpg`;
			link.href = URL.createObjectURL(blob);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.setTimeout(() => URL.revokeObjectURL(link.href), 1000);
		} catch (error) {
			console.error('Unable to export quote as JPEG:', error);
			window.alert('The quote could not be saved as a JPEG. Please try again.');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div className='quotes-page'>
			<div className='quotes-app-header'>
				<Header />
			</div>
			<main className='quotes-workspace'>
				<section className='quote-editor'>
					<div className='quote-editor-toolbar'>
						<h1>Quotes</h1>
						<div className='quote-toolbar-actions'>
							<div className='quote-mode-selector' aria-label='Quote mode'>
								<button
									className={quoteMode === 'renew' ? 'active' : ''}
									type='button'
									onClick={() => setQuoteMode('renew')}
								>
									Renew
								</button>
								<button
									className={quoteMode === 'resources' ? 'active' : ''}
									type='button'
									onClick={() => setQuoteMode('resources')}
								>
									Resources
								</button>
							</div>
							<div
								className='quote-language-selector'
								aria-label='Quote language'
							>
								<button
									className={language === 'english' ? 'active' : ''}
									type='button'
									onClick={() => setLanguage('english')}
								>
									English
								</button>
								<button
									className={language === 'spanish' ? 'active' : ''}
									type='button'
									onClick={() => setLanguage('spanish')}
								>
									Español
								</button>
							</div>
							<span>{selectedResources.length} selected</span>
							<button
								className='quote-export-button'
								type='button'
								onClick={exportAsJpeg}
								disabled={!selectedResources.length || isExporting}
							>
								{isExporting ? 'Saving JPEG…' : 'Save as JPEG'}
							</button>
						</div>
					</div>
					<div className='quote-editor-body'>
						<section className='resource-picker'>
							<div className='resource-picker-title'>
								<h2>Resources</h2>
								<button
									type='button'
									onClick={() =>
										setSelectedResources(
											selectedResources.length === quoteResources.length
												? []
												: [...quoteResources],
										)
									}
								>
									{selectedResources.length === quoteResources.length
										? 'Clear all'
										: 'Select all'}
								</button>
							</div>
							<input
								aria-label='Search resources'
								type='search'
								placeholder='Search resources…'
								value={search}
								onChange={(event) => setSearch(event.target.value)}
							/>
							<div className='resource-options'>
								{filteredResources.map((resource) => (
									<label key={resource}>
										<input
											type='checkbox'
											checked={selectedResources.includes(resource)}
											onChange={() => toggleResource(resource)}
										/>
										<span>{resource}</span>
									</label>
								))}
							</div>
						</section>
						<div className='quote-preview-area'>
							<div className='quote-preview-label'>Preview</div>
							<QuoteDocument
								documentRef={quoteDocumentRef}
								language={language}
								mode={quoteMode}
								resources={selectedResources}
							/>
						</div>
					</div>
				</section>
			</main>
		</div>
	);
};

export default QuotesPage;
